import React, { useState, useEffect, useRef } from "react";
import {
  Page,
  Card,
  Text,
  Box,
  Button,
  Checkbox,
  InlineStack,
  BlockStack,
  ProgressBar,
  Banner,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SetupTabs from "./SetupTabs";
import GeneratedContentPreviewModal from "../Modals/GeneratedContentPreviewModal";

const USER_MESSAGES = {
  fetchFailed:
    "We couldn’t load article suggestions right now. Please try again.",
  regenerateFailed:
    "We couldn’t regenerate ideas right now. Please try again.",
  noSuggestions:
    "No suggestions are available yet. Please try regenerating ideas.",
};

function ArticleSuggestions({ shopOrigin, apiKey }) {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [step, setStep] = useState(3);
  const [progress, setProgress] = useState(0);
  const [progressVisible, setProgressVisible] = useState(false);
  // const [showBanner, setShowBanner] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [previewArticle, setPreviewArticle] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState("");

  // Ref for first checkbox to focus on validation error
  // const firstCheckboxInputRef = useRef(null);
  // Banner auto-dismiss timeout ref
  // const bannerTimeoutRef = useRef(null);

  // Banner auto-hide effect with fade
  // useEffect(() => {
  //   if (showBanner) {
  //     bannerTimeoutRef.current = setTimeout(() => {
  //       setShowBanner(false);
  //     }, 5000); // 5 seconds

  //     return () => clearTimeout(bannerTimeoutRef.current);
  //   }
  // }, [showBanner]);


  function startProgressBar(onComplete) {
    setProgressVisible(true);
    let progressValue = 0;
    const animatedDuration = 30000; // 30 seconds
    const intervalTime = 50;
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      progressValue = Math.min(98, (elapsedTime / animatedDuration) * 98);
      setProgress(progressValue);

      if (progressValue >= 98 || elapsedTime >= animatedDuration) {
        clearInterval(interval);
        setProgress(98);
      }
    }, intervalTime);

    return () => {
      clearInterval(interval);
      setProgress(100);
      setTimeout(() => {
        setProgressVisible(false);
        onComplete && onComplete();
      }, 500);
    };
  }

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(
          `/api/get-generated-articles/?shop=${encodeURIComponent(
            shopOrigin || ""
          )}`,
          {
            headers: { "Content-Type": "application/json" },
          }
        );
        if (response.data.status === "success" && response.data.suggestions) {
          setArticles(response.data.suggestions);
          setSelectedArticle(null);
          setError("");
          // console.log("Fetched generated articles:", response.data.suggestions);
        } else {
          setError(response.data.message || USER_MESSAGES.noSuggestions);
          setArticles([]);
        }
      } catch (err) {
        console.error(
          "Error fetching generated articles:",
          err.response?.data || err.message
        );
        setError(err.response?.data?.message || USER_MESSAGES.fetchFailed);
        setArticles([]);
      }
    };

    fetchArticles();
  }, [shopOrigin]);

  const handleGenerateArticle = (selected) => {
    if (!selected) return;

    // setShowBanner(false);
    navigate(`/edit-blogs?shop=${encodeURIComponent(shopOrigin || "")}`, {
      state: {
        article: selected,
        language: selected.language || "English",
      },
    });
  };

  const handlePreviewArticle = (selected) => {
    if (!selected) return;
    setPreviewArticle(selected);
    setModalOpen(true);
  };

  const handleRegenerateIdeas = async () => {
    setIsRegenerating(true);
    let completeProgressBar = startProgressBar(() => {
      setIsRegenerating(false);
    });

    try {
      const response = await axios.get(
        `/api/regenerate-titles/${encodeURIComponent(shopOrigin || "")}/`,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (completeProgressBar) completeProgressBar();

      if (response.data.status === "success" && response.data.title_teasers) {
        setError("");
        navigate(`/regenerate-title-combos?shop=${encodeURIComponent(shopOrigin || "")}`, {
          state: { titleTeasers: response.data.title_teasers },
        });
      } else {
        console.error("No title teasers found:", response.data.message);
        setError(response.data.message || USER_MESSAGES.noSuggestions);
        setIsRegenerating(false);
      }
    } catch (err) {
      console.error(
        "Error regenerating titles:",
        err.response?.data || err.message
      );
      setError(err.response?.data?.message || USER_MESSAGES.regenerateFailed);
      if (completeProgressBar) completeProgressBar();
      setIsRegenerating(false);
    }
  };

  const customStyles = `
    .Polaris-Checkbox__Input:focus-visible {
      outline: 3px solid #4c9aff !important;
      outline-offset: 2px !important;
    }
  `;

  return (
    <Page title="BlogHero Setup">
      <SetupTabs step={step} setStep={setStep} />
      <Card padding={{ xs: "600", sm: "800" }} sectioned>
        <Box paddingBlockEnd="400">
          <Text variant="headingMd" as="h2">
            AI-Generated Article Suggestions
          </Text>
          <Text color="subdued" variant="bodyMd" as="p">
            Select an article to generate or regenerate new ideas
          </Text>
        </Box>

        <Box paddingBlockEnd="400">
          {error && (
            <Box paddingBlockEnd="300">
              <Banner status="critical">
                <p>{error}</p>
              </Banner>
            </Box>
          )}
          {articles.length > 0 ? (
            articles.map((article, index) => (
              <Card sectioned key={index}>
                <style>{customStyles}</style>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <InlineStack blockAlign="center" spacing="loose">
                    {/* <Checkbox
                      label=""
                      id={`article-checkbox-${index}`}
                      // ref={index === 0 ? firstCheckboxInputRef : null} // reference to the first checkbox
                      checked={selectedArticle === article.title}
                      onChange={() => {
                        if (selectedArticle === article.title) {
                          setSelectedArticle(null);
                        } else {
                          setSelectedArticle(article.title);
                        }
                      }}
                    /> */}
                    <BlockStack gap="100">
                      <Text variant="headingMd">{article.title}</Text>
                      <Text>{article.descriptive}</Text>
                      <Text variant="bodySm" color="subdued">
                        {article.readtime}
                      </Text>
                    </BlockStack>
                  </InlineStack>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="primary"
                      disabled={isRegenerating}
                      onClick={() => {
                        setSelectedArticle(article.title);
                        handleGenerateArticle(article);
                      }}
                    >
                      Generate Article
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <Text variant="bodyMd" as="p">
              No articles available. Try regenerating ideas.
            </Text>
          )}
        </Box>
        <Box>
          {progressVisible && <ProgressBar progress={progress} size="small" />}
        </Box>
      </Card>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <Button
          size="medium"
          onClick={() =>
            navigate(
              `/content-setup?shop=${encodeURIComponent(shopOrigin || "")}`
            )
          }
          disabled={isRegenerating}
        >
          Back
        </Button>
        <div style={{ display: "flex", gap: "15px" }}>
          <Button
            size="medium"
            variant="secondary"
            onClick={() => {
              // fallback to first article if none selected
              const selected = selectedArticle
                ? articles.find((a) => a.title === selectedArticle)
                : articles[0];
              setSelectedArticle(selected?.title || null);
              handlePreviewArticle(selected);
            }}
            disabled={isRegenerating}
          >
            Preview Article
          </Button>
          <Button
            size="medium"
            variant="primary"
            onClick={handleRegenerateIdeas}
            disabled={isRegenerating}
          >
            Regenerate Ideas
          </Button>
        </div>
      </div>

      <GeneratedContentPreviewModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        article={previewArticle}
      />
    </Page>
  );
}

export default ArticleSuggestions;
